import { useState, useEffect } from 'react'
import { useCrosswordStore } from '../store'
import { findAllCrossingsForPair } from '../lib/search'
import type { CrossingWithPositions } from '../lib/search'
import { getLetters } from '../lib/pairings'
import { StatusBadge, StatusIcon, PAIRING_STATUS_CONFIG } from './StatusFilter'
import { CrosswordPair } from './CrosswordText'
import type { Pairing, ClueStatus, PairingStatus, Word } from '../types'

type Props = {
  pairing: Pairing
  onShowWord?: (wordId: string) => void
}

type Orientation = 'horizontal' | 'vertical'

// Mini crossword grid showing two word pairs crossing
export function DiptychGrid({
  mainWordA,
  mainWordB,
  crossWordA,
  crossWordB,
  crossPosition,
  orientation = 'horizontal',
  maxCrossLength = 10
}: {
  mainWordA: Word
  mainWordB: Word
  crossWordA: Word
  crossWordB: Word
  crossPosition: number
  orientation?: Orientation
  maxCrossLength?: number
}) {
  const mainLettersA = getLetters(mainWordA)
  const mainLettersB = getLetters(mainWordB)
  const crossLettersA = getLetters(crossWordA)
  const crossLettersB = getLetters(crossWordB)

  // Find where in the crossing word the intersection happens
  const mainLetterA = mainLettersA[crossPosition]
  const mainLetterB = mainLettersB[crossPosition]

  let crossIndex = -1
  for (let i = 0; i < crossLettersA.length; i++) {
    if (crossLettersA[i] === mainLetterA && crossLettersB[i] === mainLetterB) {
      crossIndex = i
      break
    }
  }

  if (crossIndex === -1) return null

  const CELL = 14 // px per cell
  const GAP = 1 // px gap between cells
  const STEP = CELL + GAP

  const mainLen = mainLettersA.length
  const crossLen = crossLettersA.length

  // Use maxCrossLength for consistent horizontal positioning only
  const horizontalGridSize = Math.max(maxCrossLength, crossLen, mainLen)

  const normalCell = 'border-gray-300 bg-white text-gray-900'
  const highlightCell = 'border-amber-400 bg-amber-100 text-gray-900'

  const renderGrid = (mainLetters: string, crossLetters: string) => {
    if (orientation === 'horizontal') {
      // Main word is horizontal, cross word is vertical
      // Height based on actual cross word length (no padding)
      // Main word Y position = where crossIndex falls in the cross word
      const mainY = crossIndex
      const crossX = crossPosition

      const gridWidth = mainLen * STEP
      const gridHeight = crossLen * STEP

      return (
        <div className="relative" style={{ width: gridWidth, height: gridHeight }}>
          {/* Horizontal main word */}
          {mainLetters.split('').map((letter, idx) => {
            const isIntersection = idx === crossPosition
            return (
              <div
                key={`h-${idx}`}
                className={`absolute flex items-center justify-center border rounded-[2px] text-[9px] font-medium ${isIntersection ? highlightCell : normalCell}`}
                style={{
                  width: CELL,
                  height: CELL,
                  left: idx * STEP,
                  top: mainY * STEP,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                {letter.toUpperCase()}
              </div>
            )
          })}
          {/* Vertical cross word - skip the crossing point */}
          {crossLetters.split('').map((letter, idx) => {
            if (idx === crossIndex) return null
            return (
              <div
                key={`v-${idx}`}
                className={`absolute flex items-center justify-center border rounded-[2px] text-[9px] font-medium ${normalCell}`}
                style={{
                  width: CELL,
                  height: CELL,
                  left: crossX * STEP,
                  top: idx * STEP,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                {letter.toUpperCase()}
              </div>
            )
          })}
        </div>
      )
    } else {
      // Main word is vertical (fixed column), cross word is horizontal
      // Use horizontalGridSize for consistent horizontal positioning
      const mainX = Math.floor(horizontalGridSize / 2)
      const crossY = crossPosition
      const crossStartX = mainX - crossIndex

      const gridWidth = horizontalGridSize * STEP
      const gridHeight = mainLen * STEP

      return (
        <div className="relative" style={{ width: gridWidth, height: gridHeight }}>
          {/* Vertical main word (fixed X position) */}
          {mainLetters.split('').map((letter, idx) => {
            const isIntersection = idx === crossPosition
            return (
              <div
                key={`v-${idx}`}
                className={`absolute flex items-center justify-center border rounded-[2px] text-[9px] font-medium ${isIntersection ? highlightCell : normalCell}`}
                style={{
                  width: CELL,
                  height: CELL,
                  left: mainX * STEP,
                  top: idx * STEP,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                {letter.toUpperCase()}
              </div>
            )
          })}
          {/* Horizontal cross word - skip the crossing point */}
          {crossLetters.split('').map((letter, idx) => {
            if (idx === crossIndex) return null
            const x = crossStartX + idx
            if (x < 0 || x >= horizontalGridSize) return null
            return (
              <div
                key={`h-${idx}`}
                className={`absolute flex items-center justify-center border rounded-[2px] text-[9px] font-medium ${normalCell}`}
                style={{
                  width: CELL,
                  height: CELL,
                  left: x * STEP,
                  top: crossY * STEP,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                {letter.toUpperCase()}
              </div>
            )
          })}
        </div>
      )
    }
  }

  return (
    <div className="inline-flex gap-2.5">
      {renderGrid(mainLettersA, crossLettersA)}
      {renderGrid(mainLettersB, crossLettersB)}
    </div>
  )
}

export function CrosswordViewColumn({ pairing, onShowWord }: Props) {
  const {
    words,
    pairings,
    updatePairingClue,
    updatePairingStatus,
    getOrCreatePairing,
    setSelectedPairing,
    deletePairing
  } = useCrosswordStore()

  const [selectedPosition, setSelectedPosition] = useState<number | null>(null)
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null)
  const [clueText, setClueText] = useState(pairing.clue?.text || '')
  const [clueStatus, setClueStatus] = useState<ClueStatus>(pairing.clue?.status || 'draft')
  const [statusFilter, setStatusFilter] = useState<PairingStatus | 'all'>('all')
  const [orientation, setOrientation] = useState<Orientation>('horizontal')
  const wordA = words.find(w => w.id === pairing.wordAId)
  const wordB = words.find(w => w.id === pairing.wordBId)

  // Update local state when pairing changes
  useEffect(() => {
    setClueText(pairing.clue?.text || '')
    setClueStatus(pairing.clue?.status || 'draft')
    setSelectedPosition(null)
  }, [pairing.id])

  // Find ALL possible crossings for this pair
  const allCrossings = (() => {
    if (!wordA || !wordB) return []
    return findAllCrossingsForPair(wordA, wordB, words, pairings)
  })()

  // Filter and sort crossings by selected position and status
  const filteredCrossings = (() => {
    let result = allCrossings

    // Filter by position if selected
    if (selectedPosition !== null) {
      result = result.filter(c => c.positions.includes(selectedPosition))
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(c =>
        c.pairing ? c.pairing.status === statusFilter : statusFilter === 'none'
      )
    }

    // Sort: status first (has status > no status), rejected last, then by word length descending
    const statusOrder: Record<string, number> = {
      'approved': 1, 'draft': 2, 'none': 3, 'rejected': 4
    }

    result = [...result].sort((a, b) => {
      const statusA = a.pairing?.status || 'none'
      const statusB = b.pairing?.status || 'none'

      // First sort by status
      if (statusOrder[statusA] !== undefined && statusOrder[statusB] !== undefined && statusOrder[statusA] - statusOrder[statusB] !== 0) {
        return statusOrder[statusA] - statusOrder[statusB]
      }

      // Then by word length descending
      const lenA = getLetters(a.wordA).length
      const lenB = getLetters(b.wordA).length
      return lenB - lenA
    })

    return result
  })()

  // Calculate max crossing word length for consistent grid sizing
  const maxCrossLength = Math.max(
    10, // minimum
    ...allCrossings.map(c => getLetters(c.wordA).length)
  )

  if (!wordA || !wordB) {
    return <div className="p-4 text-gray-500">Words not found</div>
  }

  const lettersA = getLetters(wordA)
  const lettersB = getLetters(wordB)

  const handleSaveClue = () => {
    if (clueText.trim()) {
      updatePairingClue(pairing.id, clueText, clueStatus)
    }
  }

  const handleSelectCrossing = (crossing: CrossingWithPositions) => {
    const crossingPairing = crossing.pairing || getOrCreatePairing(crossing.wordA.id, crossing.wordB.id)
    if (crossingPairing) {
      setSelectedPairing(crossingPairing.id)
    }
  }

  const handleLetterClick = (idx: number) => {
    setSelectedPosition(selectedPosition === idx ? null : idx)
  }

  const handleDeletePairing = () => {
    if (confirm('Delete this pairing?')) {
      deletePairing(pairing.id)
      setSelectedPairing(null)
    }
  }

  // Get group members for each word

  const statusOptions: (PairingStatus | 'all')[] = ['all', 'none', 'draft', 'approved', 'rejected']

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header with pairing info and clue */}
      <div className="p-4 bg-gray-50/50 shrink-0">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">{lettersA.length} letters</span>
            <StatusBadge status={pairing.status} />
          </div>

          {/* Quick status buttons */}
          <div className="flex gap-1">
            {(['approved', 'draft', 'rejected'] as PairingStatus[]).map(status => {
              const config = PAIRING_STATUS_CONFIG[status]
              return (
                <button
                  key={status}
                  onClick={() => updatePairingStatus(pairing.id, status === pairing.status ? 'none' : status)}
                  className={`w-8 h-8 text-sm rounded-lg transition-all flex items-center justify-center ${pairing.status === status
                    ? config.color
                    : 'bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-600 shadow-sm'
                    }`}
                >
                  <StatusIcon status={status} />
                </button>
              )
            })}
          </div>
        </div>

        {/* Clue editor */}
        <div className="flex gap-2">
          <input
            type="text"
            value={clueText}
            onChange={e => setClueText(e.target.value)}
            onBlur={handleSaveClue}
            onKeyDown={e => e.key === 'Enter' && handleSaveClue()}
            placeholder="Enter clue..."
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-gray-400"
          />
          <select
            value={clueStatus}
            onChange={e => {
              setClueStatus(e.target.value as ClueStatus)
              if (clueText.trim()) {
                updatePairingClue(pairing.id, clueText, e.target.value as ClueStatus)
              }
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
          >
            <option value="draft">Draft</option>
            <option value="final">Final</option>
          </select>
        </div>

        {/* Delete pairing button */}
        <div className="mt-3">
          <button
            onClick={handleDeletePairing}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            Delete pairing
          </button>
        </div>
      </div>

      {/* Interactive letter display - click to filter */}
      <div className="px-4 py-3 bg-white shrink-0">
        <div className="flex items-center gap-4">
          <CrosswordPair
            textA={lettersA}
            textB={lettersB}
            autoHighlight
            highlightIndex={selectedPosition ?? hoveredPosition}
            onLetterClick={handleLetterClick}
            onLetterHover={setHoveredPosition}
          />
          {onShowWord && (
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => onShowWord(wordA.id)}
                className="px-1.5 py-0.5 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
                title={`Open word panel for ${wordA.text}`}
              >
                ↗ A
              </button>
              <button
                onClick={() => onShowWord(wordB.id)}
                className="px-1.5 py-0.5 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
                title={`Open word panel for ${wordB.text}`}
              >
                ↗ B
              </button>
            </div>
          )}
          {selectedPosition !== null && (
            <button
              onClick={() => setSelectedPosition(null)}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Clear filter
            </button>
          )}
        </div>
      </div>

      {/* Crossings list header */}
      <div className="px-4 py-2.5 bg-gray-50/50 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            {filteredCrossings.length} crossings
          </span>
          <button
            onClick={() => setOrientation(o => o === 'horizontal' ? 'vertical' : 'horizontal')}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
            title={`Switch to ${orientation === 'horizontal' ? 'vertical' : 'horizontal'}`}
          >
            {orientation === 'horizontal' ? '━' : '┃'}
          </button>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as PairingStatus | 'all')}
          className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md bg-white"
        >
          {statusOptions.map(status => (
            <option key={status} value={status}>
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Scrollable crossings list */}
      <div className="flex-1 overflow-y-auto bg-gray-50/30 p-3">
        <div className="flex flex-col gap-2">
          {filteredCrossings.map((crossing) => (
            <button
              key={`${crossing.wordA.id}-${crossing.wordB.id}`}
              onClick={() => handleSelectCrossing(crossing)}
              className="p-3 rounded-xl bg-white hover:bg-gray-50 transition-all hover:shadow-sm flex items-center justify-center relative group"
            >
              {crossing.pairing && crossing.pairing.status !== 'none' && (
                <div className="absolute top-2 right-2">
                  <StatusBadge status={crossing.pairing.status} compact />
                </div>
              )}
              <DiptychGrid
                mainWordA={wordA}
                mainWordB={wordB}
                crossWordA={crossing.wordA}
                crossWordB={crossing.wordB}
                crossPosition={crossing.positions[0] ?? 0}
                orientation={orientation}
                maxCrossLength={maxCrossLength}
              />
            </button>
          ))}
        </div>

        {filteredCrossings.length === 0 && (
          <p className="text-center py-12 text-gray-400 text-sm">
            No crossings found
          </p>
        )}
      </div>
    </div>
  )
}
